import { Component, ChangeDetectorRef, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MessageModule } from 'primeng/message';
import { MonthlyReportService } from '../../services/monthly-report.service';
import { DataService } from '../../services/data';
import { ToastService } from '../../services/toast.service';
import { SpinnerService } from '../../services/spinner.service';
import { Funciones } from '../../services/funciones';
import { Informes, InformeMensualPdfPayload, InformeItemBasePayload } from '../../models/informes.model';
import { Subscription, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Router } from '@angular/router';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { getBytes, getStorage, ref } from 'firebase/storage';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';
import { MissionCurrencyService } from '../../services/mission-currency.service';
import { MissionMoneyPipe } from '../../pipes/mission-money.pipe';
import { Iglesia } from '../../models/iglesia.model';

interface InformeMensualPdfResponse {
    base64?: string;
    contentType?: string;
    fileName?: string;
}

@Component({
    selector: 'app-reports-old',
    imports: [CommonModule, FormsModule, TableModule, TagModule, ButtonModule, InputTextModule, ToastModule, TextareaModule, DatePickerModule, FloatLabelModule, MessageModule, TranslateModule, MissionMoneyPipe],
    templateUrl: './reports-old.html',
    styleUrl: './reports-old.css',
    encapsulation: ViewEncapsulation.None,
})
export class ReportsOld implements OnInit, OnDestroy {
    private subscriptions = new Subscription();

    churchId = '';
    selectedMonth: Date | null = null;
    periodoSeleccionado = '';
    periodoDisplay = '';

    loadingReport = false;
    loadingPdf = false;
    isExporting = false;

    private reportAvailable = false;

    iglesiaName = '';
    pastorName = '';
    observaciones = '';
    imageFirmaUrl: string | null = null;
    signatureImageError = false;
    iglesiaData: Iglesia | null = null;

    /** Auditoría de cierre — solo lectura, Pastor/Admin */
    canViewLogs = false;
    informeClosedByName = '';
    informeClosedAtLabel = '';

    informeEspiritualOptions: Informes[] = [];
    informeObreoOptions: Informes[] = [];
    informeFinancieroIngresosOptions: Informes[] = [];
    informeFinancieroEgresosB1Options: Informes[] = [];
    informeFinancieroEgresosB2Options: Informes[] = [];
    informeFinancieroBalanceOptions: Informes[] = [];

    // Guardaremos la configuración dinámica de la misión (Settings/informes)
    private informeConfigDinamico: any = null;

    constructor(
        private reportService: MonthlyReportService,
        private dataService: DataService,
        private toastService: ToastService,
        private spinnerService: SpinnerService,
        private funciones: Funciones,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private languageService: LanguageService,
        private translate: TranslateService,
        private readonly missionCurrency: MissionCurrencyService,
    ) { }

    get reportLoaded(): boolean {
        return this.reportAvailable;
    }

    /** Mes/año mostrado según idioma (periodo YYYY-MM). */
    get periodoDisplayUi(): string {
        const ym = (this.periodoSeleccionado || '').trim();
        if (!ym || !/^\d{4}-\d{2}$/.test(ym)) {
            return this.periodoDisplay || '';
        }
        const [y, m] = ym.split('-').map(Number);
        if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) {
            return this.periodoDisplay || '';
        }
        const locale = this.languageService.getCurrentLang() === 'it' ? 'it-IT' : 'es-ES';
        return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }

    get hasSignatureImage(): boolean {
        return !!this.normalizeSignatureUrl(this.imageFirmaUrl) && !this.signatureImageError;
    }

    handleSignatureImageError(): void {
        this.signatureImageError = true;
        this.cdr.markForCheck();
    }

    private normalizeSignatureUrl(value: unknown): string | null {
        if (typeof value !== 'string') return null;
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
    }

    private setSignatureUrl(value: unknown): void {
        this.imageFirmaUrl = this.normalizeSignatureUrl(value);
        this.signatureImageError = false;
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.dataService.churchId$.subscribe(id => {
                this.churchId = id ?? '';
            })
        );

        this.subscriptions.add(
            this.dataService.userRoles$.subscribe(roles => {
                this.canViewLogs = (roles ?? []).some((role: string) => ['Pastor', 'Admin'].includes(role));
            })
        );

        this.subscriptions.add(
            this.dataService.dataChurch$.subscribe(church => {
                if (!church) {
                    this.iglesiaData = null;
                    return;
                }
                this.iglesiaData = church;
                if (!this.iglesiaName) {
                    this.iglesiaName = church.churchName ?? '';
                }
                if (!this.pastorName) {
                    this.pastorName = church.pastorName ?? '';
                }
                this.cdr.markForCheck();
            })
        );

        this.subscriptions.add(
            this.translate.onLangChange.subscribe(() => this.cdr.detectChanges())
        );

        this.subscriptions.add(
            this.dataService.informeConfig$.subscribe(config => {
                this.informeConfigDinamico = config;
                // Inicializamos los arreglos de opciones base si estamos en pantalla vacía
                if (!this.reportAvailable) {
                    this.resetReport();
                }
            })
        );

        this.subscriptions.add(
            this.dataService.churchSignatureUrl$.subscribe(url => {
                // Usar firma actual solo si no hay un reporte cargado con firma guardada
                if (!this.reportAvailable) {
                    this.setSignatureUrl(url);
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    onMonthChange(date: Date | null): void {
        this.selectedMonth = date;
        if (!date || !this.churchId) {
            this.periodoSeleccionado = '';
            this.periodoDisplay = '';
            this.reportAvailable = false;
            this.resetReport();
            return;
        }

        const periodo = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
        this.periodoSeleccionado = periodo;
        this.periodoDisplay = this.funciones.formatYMtoText(periodo);
        this.reportAvailable = false;
        this.fetchReport(periodo);
    }

    atras(): void {
        this.router.navigate(['/dashboard/reports']);
    }

    async exportarPDF(): Promise<void> {
        if (!this.reportLoaded || this.loadingPdf) {
            return;
        }

        this.isExporting = true;
        this.loadingPdf = true;
        this.spinnerService.show();

        try {
            const uiLang = this.languageService.getCurrentLang() === 'it' ? 'it' : 'es';
            const payload = this.buildInformeMensualPdfPayload(uiLang);
            const functions = getFunctions();
            const generarPdfCallable = httpsCallable<InformeMensualPdfPayload, InformeMensualPdfResponse>(functions, 'generarInformeMensualPdf');
            const response = await generarPdfCallable(payload);
            const data = response?.data;

            if (!data?.base64) {
                throw new Error(this.translate.instant('REPORTS.PDF_ERR_NO_BASE64'));
            }

            const contentType = data.contentType ?? 'application/pdf';
            const fileName = data.fileName ?? `informe-${this.periodoSeleccionado}.pdf`;
            this.triggerPdfDownload(data.base64, contentType, fileName);
            this.toastService.showSuccess(this.translate.instant('REPORTS.TOAST_PDF_OK'));
        } catch (error: any) {
            this.toastService.showError(error?.message || this.translate.instant('REPORTS.TOAST_PDF_ERR'));
        } finally {
            this.loadingPdf = false;
            this.spinnerService.hide();
            this.isExporting = false;
        }
    }

    /** Misma lógica que `reports`: Firestore i18n + filas fijas + fallback IT. */
    getInformeLabel(codigo: string | undefined, descripcion: string): string {
        const es = descripcion ?? '';
        if (this.languageService.getCurrentLang() !== 'it') return es;
        const fromDict = this.languageService.getItemLabel(codigo, es);
        if (fromDict !== es) return fromDict;
        const rowKey = this.financialTotalsRowKey(codigo);
        if (rowKey) return this.translate.instant(rowKey);
        return this.translateStaticInformeLabelIt(es);
    }

    private financialTotalsRowKey(codigo: string | undefined): string | null {
        const keys: Record<string, string> = {
            'ING-SUB': 'REPORTS.ROW_ING_SUB',
            'ING-TOTAL': 'REPORTS.ROW_ING_TOTAL',
            'EG-B1-SUB': 'REPORTS.ROW_EG_B1_SUB',
            'EG-B2-SUB': 'REPORTS.ROW_EG_B2_SUB',
            'EG-B2-TOTAL': 'REPORTS.ROW_EG_B2_TOTAL',
            'BAL-ING-TOT': 'REPORTS.ROW_BAL_ING_TOT',
            'BAL-EGR-TOT': 'REPORTS.ROW_BAL_EGR_TOT',
            'BAL-SAL-MES': 'REPORTS.ROW_BAL_SAL_MES',
            'BAL-SAL-ANT': 'REPORTS.ROW_BAL_SAL_ANT',
            'BAL-FINAL': 'REPORTS.ROW_BAL_FINAL',
        };
        const c = String(codigo ?? '').trim();
        return keys[c] ?? null;
    }

    obreroTagLabel(affirmative: boolean): string {
        return affirmative
            ? this.translate.instant('REPORTS.OBRERO_TAG_YES')
            : this.translate.instant('REPORTS.OBRERO_TAG_NO');
    }

    private translateStaticInformeLabelIt(esLabel: string): string {
        const key = String(esLabel ?? '').trim();
        if (!key) return esLabel;

        const map: Record<string, string> = {
            'Miembros bautizados activos': 'Membri battezzati attivi',
            'Creyentes bautizados no reconocidos': 'Credenti battezzati non riconosciuti',
            'Nuevos creyentes': 'Nuovi credenti',
            'Miembros bautizados en Espíritu Santo': 'Membri battezzati nello Spirito Santo',
            'Profesiones de fe durante el mes': 'Professioni di fede durante il mese',
            'Promedio asistencia Escuela Dominical': 'Media presenza alla Scuola Domenicale',
            'Efectuó culto misionero': 'Ha tenuto culto missionario',
            'Efectuó culto Santa Cena': 'Ha tenuto culto Santa Cena',
            'Asistió a culto confraternidad': 'Ha partecipato al culto di confraternita',
            'Recibió visita del presbítero': 'Ha ricevuto visita del presbitero',
            'Efectuó visitas': 'Ha effettuato visite',
            'Realizó evangelismo': 'Ha svolto evangelizzazione',
            'Ha fundado campos blancos': 'Ha fondato campi bianchi',
            'Ha formado obreros': 'Ha formato operai',
            'Efectúa ayuda social': 'Esegue aiuto sociale',
            'Efectuó ayuno': 'Ha osservato digiuno',
        };

        return map[key] || esLabel;
    }

    isObreroAffirmative(valor: any): boolean {
        if (typeof valor === 'boolean') {
            return valor;
        } else if (typeof valor === 'number') {
            return valor === 0;
        } else {
            const rawValue = String(valor ?? '').trim();
            if (rawValue === '0') return true;
            if (rawValue === '') return false;
            const normalized = rawValue
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');
            return normalized === 'si' || normalized === 'true' || normalized === '1';
        }
    }

    private getPeriodoDisplayForPdf(uiLang: 'es' | 'it'): string {
        const ym = (this.periodoSeleccionado || '').trim();
        if (!ym || !/^\d{4}-\d{2}$/.test(ym)) {
            return this.periodoDisplay || '';
        }
        const [y, m] = ym.split('-').map(Number);
        const d = new Date(y, (m - 1), 1);
        const locale = uiLang === 'it' ? 'it-IT' : 'es-ES';
        return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }

    private getZonaNombre(): string {
        const data = this.iglesiaData as Record<string, unknown> | null;
        const z = data?.['zonaNombre'] ?? data?.['zoneName'] ?? data?.['zona'];
        return (typeof z === 'string' && z.trim()) ? z : this.translate.instant('REPORTS.ZONE_UNSPECIFIED');
    }

    private buildInformeMensualPdfPayload(uiLang: 'es' | 'it'): InformeMensualPdfPayload {
        const locale = uiLang === 'it' ? 'it-IT' : 'es-ES';
        const fechaInforme = new Date().toLocaleDateString(locale, {
            day: '2-digit', month: 'long', year: 'numeric'
        });
        const observaciones = (this.observaciones ?? '').trim();

        const mappedObrero = this.mapObreroItems(this.informeObreoOptions, uiLang);

        return {
            uiLang,
            moneda: this.missionCurrency.getCurrencyCode(),
            iglesiaName: this.iglesiaName || '',
            zonaNombre: this.getZonaNombre(),
            previousMonthName: this.getPeriodoDisplayForPdf(uiLang),
            fechaInforme,
            pastorName: this.pastorName || '',
            observaciones,
            imageFirmaUrl: this.imageFirmaUrl || undefined,
            informeEspiritualOptions: this.mapEspiritualItems(this.informeEspiritualOptions, uiLang),
            informeFinancieroIngresosOptions: this.mapFinancieroItems(this.informeFinancieroIngresosOptions, uiLang),
            informeFinancieroEgresosB1Options: this.mapFinancieroItems(this.informeFinancieroEgresosB1Options, uiLang),
            informeFinancieroEgresosB2Options: this.mapFinancieroItems(this.informeFinancieroEgresosB2Options, uiLang),
            informeFinancieroBalanceOptions: this.mapFinancieroItems(this.informeFinancieroBalanceOptions, uiLang),
            informeObreoOptions: mappedObrero,
        };
    }

    private mapFinancieroItems(list: Informes[] = [], uiLang: 'es' | 'it' = 'es'): InformeItemBasePayload[] {
        return (list ?? []).map(row => ({
            item: row.item,
            descripcion: uiLang === 'it'
                ? this.getInformeLabel(row.codigo, row.descripcion ?? '')
                : row.descripcion,
            valor: this.toNumber(row.valor),
        }));
    }

    private mapEspiritualItems(list: Informes[] = [], uiLang: 'es' | 'it' = 'es'): InformeItemBasePayload[] {
        return (list ?? []).map(row => {
            const valor = this.toNumber(row.valor);
            const es = row.descripcion ?? '';
            return {
                item: row.item,
                descripcion: uiLang === 'it' ? this.getInformeLabel(row.codigo, es) : es,
                valor,
                cumplido: valor > 0,
            } as InformeItemBasePayload;
        });
    }

    private mapObreroItems(list: Informes[] = [], uiLang: 'es' | 'it' = 'es'): InformeItemBasePayload[] {
        return (list ?? []).map(row => {
            const affirmative = this.isObreroAffirmative(row.valor);
            const es = row.descripcion ?? '';

            return {
                item: row.item,
                descripcion: uiLang === 'it' ? this.getInformeLabel(row.codigo, es) : es,
                resp: affirmative
                    ? this.translate.instant('REPORTS.OBRERO_TAG_YES')
                    : this.translate.instant('REPORTS.OBRERO_TAG_NO'),
                cumplido: affirmative,
            };
        });
    }

    private toNumber(value: number | string): number {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : 0;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    private triggerPdfDownload(base64: string, contentType: string, fileName: string) {
        if (typeof window === 'undefined') {
            return;
        }

        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: contentType });
        const url = URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
        URL.revokeObjectURL(url);
    }

    private async fetchReport(periodo: string): Promise<void> {
        this.loadingReport = true;
        this.spinnerService.show();
        try {
            const data = await this.reportService.fetchReport(this.churchId, periodo);
            if (!data) {
                this.resetReport();
                this.toastService.showWarning(this.translate.instant('REPORTS.HISTORY_TOAST_NO_REPORT'));
                this.cdr.markForCheck();
                return;
            }
            this.applyReportData(data, periodo);
            this.reportAvailable = true;
        } catch (error: any) {
            this.resetReport();
            this.toastService.showError(this.translate.instant('REPORTS.HISTORY_TOAST_FETCH_ERROR'));
        } finally {
            this.loadingReport = false;
            this.spinnerService.hide();
            this.cdr.markForCheck();
        }
    }

    private applyReportData(data: any, periodo: string): void {
        this.periodoDisplay = data?.header?.periodoDisplay ?? this.funciones.formatYMtoText(periodo);
        this.iglesiaName = data?.header?.iglesiaName ?? this.iglesiaName;
        this.pastorName = data?.header?.pastorName ?? this.pastorName;
        // Priorizar firma guardada en base64, luego URL
        const savedSignature = this.normalizeSignatureUrl(data?.firma?.base64)
            ?? this.normalizeSignatureUrl(data?.firma?.url);
        this.setSignatureUrl(savedSignature);
        this.observaciones = this.formatObservaciones(data?.observacionesTexto ?? data?.observaciones ?? '');

        // Preparamos los defaults dinámicos mezclando los locales con el config de la misión
        const protegidos = [
            'ING-SUB',
            'ING-TOTAL',
            'EG-B1-SUB',
            'EG-B2-SUB',
            'EG-B2-DP',
            'EG-B2-TOTAL',
            'BAL-ING-TOT',
            'BAL-EGR-TOT',
            'BAL-SAL-MES',
            'BAL-SAL-ANT',
            'BAL-FINAL'
        ];

        const defaultObreroDin = this.informeConfigDinamico?.obrero ? this.dataService.fusionarOpcionesInformes(this.getDefaultObrero(), this.informeConfigDinamico.obrero, protegidos) : this.getDefaultObrero();
        const defaultIngresosDin = this.informeConfigDinamico?.ingresos ? this.dataService.fusionarOpcionesInformes(this.getDefaultIngresos(), this.informeConfigDinamico.ingresos, protegidos) : this.getDefaultIngresos();
        const defaultEgresosB1Din = this.informeConfigDinamico?.egresosB1 ? this.dataService.fusionarOpcionesInformes(this.getDefaultEgresosB1(), this.informeConfigDinamico.egresosB1, protegidos) : this.getDefaultEgresosB1();
        const defaultEgresosB2Din = this.informeConfigDinamico?.egresosB2 ? this.dataService.fusionarOpcionesInformes(this.getDefaultEgresosB2(), this.informeConfigDinamico.egresosB2, protegidos) : this.getDefaultEgresosB2();

        // Si existe snapshot en Firestore, predomina (historia intacta).
        // Si NO existe snapshot, usamos los defaults dinámicos que acabamos de armar
        const defaultEspiritual = data?.itemsSnapshot?.espiritual ?? this.getDefaultEspiritual();
        const defaultObrero = data?.itemsSnapshot?.obrero ?? defaultObreroDin;
        const defaultIngresos = data?.itemsSnapshot?.financiero?.ingresos ?? defaultIngresosDin;
        const defaultEgresosB1 = data?.itemsSnapshot?.financiero?.egresosB1 ?? defaultEgresosB1Din;
        const defaultEgresosB2 = data?.itemsSnapshot?.financiero?.egresosB2 ?? defaultEgresosB2Din;
        const defaultBalance = data?.itemsSnapshot?.financiero?.balance ?? this.getDefaultBalance();

        this.informeEspiritualOptions = this.mapInformes(data?.espiritual, defaultEspiritual);
        this.informeObreoOptions = this.mapInformes(data?.obrero, defaultObrero, this.normalizeObreroValue.bind(this));
        this.informeFinancieroIngresosOptions = this.mapInformes(data?.financiero?.ingresos, defaultIngresos);
        if (!data?.itemsSnapshot?.financiero?.ingresos) {
            this.reordenarIngresosSubTotal();
        }
        this.informeFinancieroEgresosB1Options = this.mapInformes(data?.financiero?.egresosB1, defaultEgresosB1);
        this.informeFinancieroEgresosB2Options = this.mapInformes(data?.financiero?.egresosB2, defaultEgresosB2);
        this.informeFinancieroBalanceOptions = this.mapInformes(data?.financiero?.balance, defaultBalance);

        // Solo metadatos de auditoría — no altera datos del informe
        const meta = data?.metadata;
        this.informeClosedByName = meta?.closedByName || meta?.createdByUid || '';
        this.informeClosedAtLabel = this.formatAuditDate(meta?.closedAt || meta?.createdAt);
    }

    private resetReport(): void {
        // Mantener imageFirmaUrl con la firma actual de la iglesia (no limpiar)
        this.observaciones = '';
        this.reportAvailable = false;
        this.informeClosedByName = '';
        this.informeClosedAtLabel = '';

        const protegidos = [
            'ING-SUB',
            'ING-TOTAL',
            'EG-B1-SUB',
            'EG-B2-SUB',
            'EG-B2-DP',
            'EG-B2-TOTAL',
            'BAL-ING-TOT',
            'BAL-EGR-TOT',
            'BAL-SAL-MES',
            'BAL-SAL-ANT',
            'BAL-FINAL'
        ];

        this.informeEspiritualOptions = this.getDefaultEspiritual();
        this.informeObreoOptions = this.informeConfigDinamico?.obrero ? this.dataService.fusionarOpcionesInformes(this.getDefaultObrero(), this.informeConfigDinamico.obrero, protegidos) : this.getDefaultObrero();
        this.informeFinancieroIngresosOptions = this.informeConfigDinamico?.ingresos ? this.dataService.fusionarOpcionesInformes(this.getDefaultIngresos(), this.informeConfigDinamico.ingresos, protegidos) : this.getDefaultIngresos();
        this.reordenarIngresosSubTotal();
        this.informeFinancieroEgresosB1Options = this.informeConfigDinamico?.egresosB1 ? this.dataService.fusionarOpcionesInformes(this.getDefaultEgresosB1(), this.informeConfigDinamico.egresosB1, protegidos) : this.getDefaultEgresosB1();
        this.informeFinancieroEgresosB2Options = this.informeConfigDinamico?.egresosB2 ? this.dataService.fusionarOpcionesInformes(this.getDefaultEgresosB2(), this.informeConfigDinamico.egresosB2, protegidos) : this.getDefaultEgresosB2();
        this.informeFinancieroBalanceOptions = this.getDefaultBalance();
    }

    private mapInformes(source: any, defaults: Informes[], customNormalizer?: (v: any) => string): Informes[] {
        if (!Array.isArray(source) || !source.length) {
            return defaults;
        }

        const normalizedSource = source.map((row: any) => ({
            item: row?.item ?? '',
            codigo: row?.codigo ?? '',
            descripcion: row?.descripcion ?? '',
            valor: row?.valor ?? null
        }));

        const byCodigo = new Map(
            normalizedSource
                .filter(row => row.codigo)
                .map(row => [this.norm(row.codigo), row])
        );

        return defaults.map(def => {
            const match = (def.codigo && byCodigo.get(this.norm(def.codigo)))
                || normalizedSource.find(row => this.norm(row.descripcion) === this.norm(def.descripcion));

            if (!match) {
                return { ...def, valor: this.defaultValor(def.valor) };
            }

            const valor = typeof def.valor === 'number'
                ? Number(match.valor ?? 0)
                : (customNormalizer ? customNormalizer(match.valor ?? def.valor) : this.normalizeRespuesta(match.valor ?? def.valor));

            return {
                item: match.item ?? def.item,
                codigo: match.codigo ?? def.codigo,
                descripcion: match.descripcion ?? def.descripcion,
                valor
            };
        });
    }

    private reordenarIngresosSubTotal(): void {
        const list = this.informeFinancieroIngresosOptions || [];
        const subIndex = list.findIndex(r => r.codigo === 'ING-SUB');
        if (subIndex === -1) return;
        const targetIndex = list.findIndex(r => r.codigo === 'ING-03');
        if (targetIndex === -1) return;
        const [subRow] = list.splice(subIndex, 1);
        list.splice(targetIndex + 1, 0, subRow);
        this.informeFinancieroIngresosOptions = list;
    }

    private formatObservaciones(value: any): string {
        if (typeof value === 'string') {
            return value;
        }
        if (Array.isArray(value)) {
            return value
                .map((row: any) => {
                    const desc = row?.descripcion ?? '';
                    const val = row?.valor;
                    if (desc && (val || val === 0)) {
                        return `${desc}: ${val}`;
                    }
                    return desc;
                })
                .filter(Boolean)
                .join('\n');
        }
        return '';
    }

    private normalizeRespuesta(valor: any): string {
        if (typeof valor === 'string') {
            const normalized = valor.trim().toLowerCase();
            if (['true', '1', 'si', 'sí', 'yes', 'y'].includes(normalized)) {
                return 'SI';
            }
            if (['false', '0', 'no', 'n'].includes(normalized)) {
                return 'NO';
            }
            return valor.toUpperCase();
        }
        if (typeof valor === 'boolean') {
            return valor ? 'SI' : 'NO';
        }
        if (typeof valor === 'number') {
            return valor !== 0 ? 'SI' : 'NO';
        }
        return 'NO';
    }

    private normalizeObreroValue(valor: any): string {
        // Reglas específicas para Informe Obrero:
        // "0" (string) -> SI
        // "" (string) -> NO
        // true (boolean) -> SI
        // 0 (number) -> NO  (Corrección solicitada: false se guardó como number 0 en algunos casos)

        if (valor === '0') return 'SI';
        if (valor === '') return 'NO';
        if (valor === true) return 'SI';
        if (valor === 0) return 'NO';

        // Fallback genérico para otros casos
        return this.normalizeRespuesta(valor);
    }

    private defaultValor(valor: any): any {
        return typeof valor === 'number' ? 0 : 'NO';
    }

    private norm(value: string): string {
        return (value ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
    }

    private getDefaultEspiritual(): Informes[] {
        return [
            { item: 1, codigo: 'IE-01', descripcion: 'Miembros bautizados activos', valor: 0 },
            { item: 2, codigo: 'IE-02', descripcion: 'Creyentes bautizados no reconocidos', valor: 0 },
            { item: 3, codigo: 'IE-03', descripcion: 'Nuevos creyentes', valor: 0 },
            { item: 4, codigo: 'IE-04', descripcion: 'Miembros bautizados en Espíritu Santo', valor: 0 },
            { item: 5, codigo: 'IE-05', descripcion: 'Profesiones de fe durante el mes', valor: 0 },
            { item: 6, codigo: 'IE-06', descripcion: 'Promedio asistencia Escuela Dominical', valor: 0 }
        ];
    }

    private getDefaultObrero(): Informes[] {
        return [
            { item: 1, codigo: 'OB-01', descripcion: 'Efectuó culto misionero', valor: 'NO' },
            { item: 2, codigo: 'OB-02', descripcion: 'Efectuó culto Santa Cena', valor: 'NO' },
            { item: 3, codigo: 'OB-03', descripcion: 'Asistió a culto confraternidad', valor: 'NO' },
            { item: 4, codigo: 'OB-04', descripcion: 'Recibió visita del presbítero', valor: 'NO' },
            { item: 5, codigo: 'OB-05', descripcion: 'Efectuó visitas', valor: 'NO' },
            { item: 6, codigo: 'OB-06', descripcion: 'Realizó evangelismo', valor: 'NO' },
            { item: 7, codigo: 'OB-07', descripcion: 'Ha fundado campos blancos', valor: 'NO' },
            { item: 8, codigo: 'OB-08', descripcion: 'Ha formado obreros', valor: 'NO' },
            { item: 9, codigo: 'OB-09', descripcion: 'Efectúa ayuda social', valor: 'NO' },
            { item: 10, codigo: 'OB-10', descripcion: 'Efectuó ayuno', valor: 'NO' }
        ];
    }

    private getDefaultIngresos(): Informes[] {
        return [
            { item: 1, codigo: 'ING-01', descripcion: 'Diezmo iglesia', valor: 0 },
            { item: 2, codigo: 'ING-02', descripcion: 'Ofrendas diarias', valor: 0 },
            { item: 3, codigo: 'ING-03', descripcion: 'Ofrendas Escuela Dominical', valor: 0 },
            { item: '', codigo: 'ING-SUB', descripcion: 'SUB TOTAL', valor: 0 },
            { item: 4, codigo: 'ING-04', descripcion: 'Ofrendas (Programa Radial)', valor: 0 },
            { item: 5, codigo: 'ING-05', descripcion: 'Ofrenda Misionera', valor: 0 },
            { item: 6, codigo: 'ING-06', descripcion: 'Ofrenda Predicador', valor: 0 },
            { item: 7, codigo: 'ING-07', descripcion: 'Ofrenda Especial', valor: 0 },
            { item: 8, codigo: 'ING-08', descripcion: 'Ayuda Fondo Nacional/Internacional', valor: 0 },
            { item: '', codigo: 'ING-TOTAL', descripcion: 'TOTAL INGRESO GENERAL IGLESÍA', valor: 0 }
        ];
    }

    private getDefaultEgresosB1(): Informes[] {
        return [
            { item: 1, codigo: 'EG-B1-01', descripcion: 'Alquiler local Iglesia', valor: 0 },
            { item: 2, codigo: 'EG-B1-02', descripcion: 'Pago de luz, agua, gas, teléfono (Iglesia)', valor: 0 },
            { item: 3, codigo: 'EG-B1-03', descripcion: 'Pago de impuesto', valor: 0 },
            { item: 4, codigo: 'EG-B1-04', descripcion: 'Mantenimiento y mejoras del templo', valor: 0 },
            { item: 5, codigo: 'EG-B1-05', descripcion: 'Gastos de oficina, copias, envíos documentos', valor: 0 },
            { item: 6, codigo: 'EG-B1-06', descripcion: 'Compra de mob, equipos, instrumentos', valor: 0 },
            { item: 7, codigo: 'EG-B1-07', descripcion: 'Seguridad social', valor: 0 },
            { item: 8, codigo: 'EG-B1-08', descripcion: 'Alquiler casa pastoral', valor: 0 },
            { item: 9, codigo: 'EG-B1-09', descripcion: 'Pago luz, agua, gas (Casa Pastoral)', valor: 0 },
            { item: 10, codigo: 'EG-B1-10', descripcion: 'Transportes, viáticos', valor: 0 },
            { item: 11, codigo: 'EG-B1-11', descripcion: 'Pago préstamos', valor: 0 },
            { item: 12, codigo: 'EG-B1-12', descripcion: 'Programa radial', valor: 0 },
            { item: 13, codigo: 'EG-B1-13', descripcion: 'Sostén del pastor', valor: 0 },
            { item: 14, codigo: 'EG-B1-14', descripcion: 'Otros (Ayudas, comidas, Escuela Dominical)', valor: 0 },
            { item: 15, codigo: 'EG-B1-15', descripcion: 'Ofrenda al predicador', valor: 0 },
            { item: 16, codigo: 'EG-B1-16', descripcion: 'Impuesto vehículo', valor: 0 },
            { item: 17, codigo: 'EG-B1-17', descripcion: 'Mantenimiento del vehículo', valor: 0 },
            { item: 18, codigo: 'EG-B1-18', descripcion: 'Parking', valor: 0 },
            { item: '', codigo: 'EG-B1-SUB', descripcion: 'SUB-TOTAL (B.1)', valor: 0 }
        ];
    }

    private getDefaultEgresosB2(): Informes[] {
        return [
            { item: 1, codigo: 'EG-B2-01', descripcion: 'Diezmos de diezmos y ofrendas', valor: 0 },
            { item: 2, codigo: 'EG-B2-02', descripcion: 'Ofrenda Misionera', valor: 0 },
            { item: 3, codigo: 'EG-B2-03', descripcion: 'Ofrenda Pro-convención', valor: 0 },
            //{ item: 4, codigo: 'EG-B2-04', descripcion: 'Ofrenda confraternidad', valor: 0 },
            { item: 5, codigo: 'EG-B2-05', descripcion: 'Cuota FEREDE', valor: 0 },
            { item: 6, codigo: 'EG-B2-06', descripcion: 'Gestor', valor: 0 },
            //{ item: 7, codigo: 'EG-B2-07', descripcion: 'Servidor MMM', valor: 0 },
            { item: 8, codigo: 'EG-B2-08', descripcion: 'Otras ofrendas y pagos', valor: 0 },
            //{ item: 9, codigo: 'EG-B2-09', descripcion: 'Seguridad Social', valor: 0 },
            { item: 10, codigo: 'EG-B2-10', descripcion: 'Seguro de Vida', valor: 0 },
            { item: 11, codigo: 'EG-B2-11', descripcion: 'Seguro defunción', valor: 0 },
            { item: 12, codigo: 'EG-B2-12', descripcion: 'I.R.P.F.', valor: 0 },
            { item: 13, codigo: 'EG-B2-13', descripcion: 'Pago Sistema Informe', valor: 5 },
            { item: 14, codigo: 'EG-B2-14', descripcion: 'Bethel TV España', valor: 0 },
            { item: 15, codigo: 'EG-B2-15', descripcion: 'Pago Préstamo', valor: 0 },
            { item: 16, codigo: 'EG-B2-16', descripcion: 'Ofrenda Valdepeñas', valor: 0 },
            { item: '', codigo: 'EG-B2-SUB', descripcion: 'SUB-TOTAL B.2', valor: 0 },
            { item: '*', codigo: 'EG-B2-DP', descripcion: 'Diezmos del pastor', valor: 0 },
            { item: '', codigo: 'EG-B2-TOTAL', descripcion: 'TOTAL ENVIADO FONDO NACIONAL', valor: 0 }
        ];
    }

    private getDefaultBalance(): Informes[] {
        return [
            { item: '', codigo: 'BAL-ING-TOT', descripcion: 'Total General de Ingresos', valor: 0 },
            { item: '', codigo: 'BAL-EGR-TOT', descripcion: 'Total General de Egresos (B.1 + B.2)', valor: 0 },
            { item: '', codigo: 'BAL-SAL-MES', descripcion: 'Saldo en Caja del Mes', valor: 0 },
            { item: '', codigo: 'BAL-SAL-ANT', descripcion: 'Saldo en Caja Mes Anterior', valor: 0 },
            { item: '', codigo: 'BAL-FINAL', descripcion: 'SALDO FINAL DEL MES', valor: 0 }
        ];
    }

    private formatAuditDate(value: any): string {
        if (!value) return '';
        let date: Date | null = null;
        if (typeof value?.toDate === 'function') {
            date = value.toDate();
        } else if (typeof value?.seconds === 'number') {
            date = new Date(value.seconds * 1000);
        } else if (value instanceof Date) {
            date = value;
        }
        if (!date) return '';
        const locale = this.languageService.getCurrentLang() === 'it' ? 'it-IT' : 'es-ES';
        return date.toLocaleString(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }
}
